import { useTranslations } from '@lang';
import { AddIcon } from '@root/styles/icons';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { FC, useEffect } from 'react';
import idGenerator from 'react-id-generator';
import { Field } from 'redux-form';
import { genderOptions, genderOptionsWithNeutral } from '../../../constants';
import { Button } from '../../Button';
import { ReduxFormInput } from '../../FormElements/BlockInputField';
import { ReduxFormSelect } from '../../FormElements/BlockSelectField';
import { RadioGroupField } from '../../RadioGroupField';
import { GuestFieldProps } from './GuestField.types';
import RemoveGuestButton from './RemoveGuestButton';

import {
  AddButtonWrapper,
  ForenameColumn,
  GenderColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  OptionalIndicator,
  RadioWrapper,
  RemoveGuestButtonWrapper,
  SurnameColumn,
  TitleColumn,
} from './GuestField.styles';

export const GuestField: FC<GuestFieldProps> = ({
  fields,
  maximumGuests,
  isGenderSpecific = false,
  minimumGuests,
  fieldChange,
  withLeadGuest = false,
  withGenderSelect = true,
  defaultGender,
  showGenderNeutral = false,
}) => {
  const { t } = useTranslations();

  useEffect(() => {
    if (maximumGuests && fields.length > maximumGuests) {
      const amountOfRemovedItems = fields.length - maximumGuests;
      new Array(amountOfRemovedItems).fill(null).forEach(fields.pop);
    }
  });

  const handleGuestIndexRemove = (index) => {
    fields.remove(index);
    if (fields.length <= minimumGuests) {
      fields.push({
        firstName: '',
        lastName: '',
        gender: defaultGender,
      });
    }
  };

  const handleAddRow = () => {
    if (!maximumGuests || fields.length < maximumGuests) {
      fields.push({
        title: '',
        gender: defaultGender,
        value: idGenerator(),
      });
    }
  };

  const titlesOptions = [
    ...(isGenderSpecific === 'M' || isGenderSpecific !== 'F' ? maleTitlesOptions : []),
    ...(isGenderSpecific === 'F' || isGenderSpecific !== 'M' ? femaleTitlesOptions : []),
  ].map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const canAddNextGuest = !maximumGuests || (maximumGuests && fields.length < maximumGuests);

  const isSpaEntityGenderSpecific = !!isGenderSpecific;

  return (
    <>
      {fields.map((guest, index) => {
        const isExistingBasketGuest = !!fields.get(index).externalSpaId || !!fields.get(index).id;
        const isGenderDataMissing = !(!!fields.get(index).title || !!fields.get(index).gender);
        const isOptionalIndicatorVisible = index > 0 && withLeadGuest;
        const isFieldsRequired = !isOptionalIndicatorVisible;

        return (
          <GuestRow key={index}>
            <GuestCounter>
              {withLeadGuest && index === 0
                ? t('common.leadGuest')
                : t('spa.guestCounter', { counter: index + 1 })}{' '}
              {isOptionalIndicatorVisible && (
                <OptionalIndicator>{t('common.optional')}</OptionalIndicator>
              )}
            </GuestCounter>
            <InputsRow>
              <TitleColumn>
                <Field
                  isFullWidth
                  useNativeDropdownOnMobile={false}
                  component={ReduxFormSelect}
                  labelWidth="50px"
                  name={`${guest}.title`}
                  options={titlesOptions}
                  label={t('common.title')}
                  handleAfterChange={(value) => {
                    fieldChange &&
                      fieldChange(`${guest}.gender`, value.value === 'Mr' ? 'male' : 'female');
                  }}
                  isRequired={isFieldsRequired}
                  parse={(selected) => selected && selected.value}
                  format={(selected) => titlesOptions.find(({ value }) => value === selected)}
                  isDisabled={isExistingBasketGuest && !isGenderDataMissing}
                  isSearchable={false}
                  id={`${index}-title`}
                />
              </TitleColumn>
              <ForenameColumn>
                <Field
                  isFullWidth
                  component={ReduxFormInput}
                  isRequired={isFieldsRequired}
                  name={`${guest}.firstName`}
                  label={t('common.firstName')}
                  placeholder={t('common.firstName')}
                  disabled={isExistingBasketGuest}
                  id={`${index}-fname`}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Field
                  isFullWidth
                  component={ReduxFormInput}
                  isRequired={isFieldsRequired}
                  name={`${guest}.lastName`}
                  label={t('common.lastName')}
                  placeholder={t('common.lastName')}
                  disabled={isExistingBasketGuest}
                  id={`${index}-lname`}
                />
              </SurnameColumn>
              {!isSpaEntityGenderSpecific && withGenderSelect && (
                <GenderColumn>
                  <RadioWrapper>
                    <Field
                      name={`${guest}.gender`}
                      component={RadioGroupField}
                      cols={3}
                      options={showGenderNeutral ? genderOptionsWithNeutral : genderOptions}
                      isDisabled={!showGenderNeutral && isExistingBasketGuest}
                    />
                  </RadioWrapper>
                </GenderColumn>
              )}
              <RemoveGuestButtonWrapper>
                <RemoveGuestButton
                  isVisible={isExistingBasketGuest || index >= minimumGuests}
                  onClick={() => handleGuestIndexRemove(index)}
                />
              </RemoveGuestButtonWrapper>
            </InputsRow>
          </GuestRow>
        );
      })}
      <AddButtonWrapper>
        {canAddNextGuest && (
          <Button
            type="button"
            isSmall
            onClick={handleAddRow}
            icon={<AddIcon width="16" height="16" />}
          >
            {t('common.addMoreGuests')}
          </Button>
        )}
      </AddButtonWrapper>
    </>
  );
};
